import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, PackageX, FileSpreadsheet, FileText, ChevronDown, ChevronRight, Upload, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSortable, SortableTh } from "@/lib/sort";
import { FarmanalizaStalledBanner } from "@/components/stock/FarmanalizaStalledBanner";

const fmtEur = (v) => `${(Number(v) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const MONTH_OPTIONS = [1, 3, 6, 9, 12];

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");

const LastSale = ({ item }) => {
  if (!item?.last_sale_date) {
    return <span className="text-slate-400 italic whitespace-nowrap">Sin venta en el periodo</span>;
  }
  return (
    <span className="whitespace-nowrap">
      <span className="font-mono-num font-semibold text-slate-700">{fmtDate(item.last_sale_date)}</span>
      {item.last_sale_units > 0 && <span className="text-slate-500"> · {item.last_sale_units} uds</span>}
      {!item.last_sale_exact && (
        <span className="text-[10px] uppercase text-slate-400 ml-1" title={`Fecha aproximada: fin del estudio ${item.last_sale_label || ""}`}>
          aprox.
        </span>
      )}
    </span>
  );
};

export default function StalledStockPage() {
  const [dispId, setDispId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("generico");
  const [months, setMonths] = useState(1);
  const [open, setOpen] = useState({});
  const [showAll, setShowAll] = useState(false);

  const fetchData = useCallback(async (id, m, mo) => {
    setLoading(true);
    try {
      const r = await api.get(`/dispensations/${id}/stalled-stock?mode=${m}&months=${mo}`);
      setData(r.data);
      setOpen({});
      setShowAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo calcular el stock parado");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const list = await api.get("/dispensations");
        const latest = (list.data || [])[0];
        if (!latest) {
          toast.info("Todavía no has cargado ningún estudio.");
          setLoading(false);
          return;
        }
        setDispId(latest.id);
        await fetchData(latest.id, "generico", 1);
      } catch (e) {
        toast.error("No se pudieron cargar los estudios");
        setLoading(false);
      }
    };
    boot();
  }, [fetchData]);

  const switchTo = (m, mo = months) => {
    setMode(m);
    setMonths(mo);
    if (dispId) fetchData(dispId, m, mo);
  };

  const download = async (fmt) => {
    const t = toast.loading(fmt === "pdf" ? "Generando PDF…" : "Generando Excel…");
    try {
      const r = await api.get(`/dispensations/${dispId}/stalled-stock?mode=${mode}&months=${months}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${mode === "marca" ? `marcas_paradas_${months}m` : "genericos_parados"}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargado", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descargar", { id: t });
    }
  };

  const isMarca = mode === "marca";
  const mols = (data?.molecules || []).map((m) => ({ ...m, lab_text: (m.laboratorios || []).join(", ") }));
  const { sorted, sortKey, direction, toggle } = useSortable(mols, "stock_eur", "desc", "stalled-stock");
  const visible = showAll ? sorted : sorted.slice(0, 60);

  if (data?.no_stock) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center space-y-4" data-testid="stalled-no-stock">
        <PackageX className="h-10 w-10 mx-auto text-slate-300" strokeWidth={1.4} />
        <h1 className="text-2xl font-bold text-slate-900">Aún no has subido tu stock</h1>
        <p className="text-sm text-slate-500">
          Para saber qué tienes parado necesito el fichero de existencias de tu programa de gestión.
        </p>
        <Link to="/stock" className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium" data-testid="stalled-go-upload">
          <Upload className="h-4 w-4" /> Subir stock
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stalled-stock-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {isMarca ? "Marcas paradas" : "Genéricos con stock que no vendes"}
          </h1>
          <p className="text-base text-slate-500 mt-1">
            {isMarca ? (
              <>Marcas con existencias y <b>sin ninguna salida</b> en los últimos {months} {months === 1 ? "mes" : "meses"}.</>
            ) : (
              <>Moléculas clasificadas <b>SOLO MARCA</b> de las que tienes el genérico en la balda y no despachas ninguna unidad · {data?.label || "estudio vigente"}.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isMarca && (
            <button onClick={() => switchTo("generico", 1)} data-testid="stalled-back-generico"
              className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Volver a genéricos
            </button>
          )}
          <button onClick={() => download("xlsx")} disabled={!mols.length} data-testid="stalled-export-xlsx"
            className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-emerald-50 disabled:opacity-40">
            <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Excel
          </button>
          <button onClick={() => download("pdf")} disabled={!mols.length} data-testid="stalled-export-pdf"
            className="inline-flex items-center gap-2 bg-white border border-rose-300 text-rose-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-rose-50 disabled:opacity-40">
            <FileText className="h-4 w-4" strokeWidth={1.75} /> PDF
          </button>
        </div>
      </div>

      {/* Acceso a la vista de marca parada con elección de meses */}
      <div className="bg-slate-950 text-white rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="stalled-marca-switch">
        <div className="flex-1">
          <div className="text-sm font-bold uppercase tracking-wider">¿Quieres ver la marca parada?</div>
          <div className="text-xs text-slate-300 mt-0.5">
            Elige cuántos meses miramos hacia atrás y te saco las marcas con stock que no han salido.
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              onClick={() => switchTo("marca", m)}
              data-testid={`stalled-months-${m}`}
              className={`px-3.5 py-2 rounded-md text-sm font-bold font-mono-num transition-colors ${
                isMarca && months === m ? "bg-white text-slate-950" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {m} {m === 1 ? "mes" : "meses"}
            </button>
          ))}
        </div>
      </div>

      {!loading && mols.length > 0 && (
        <FarmanalizaStalledBanner count={mols.length} value={mols.reduce((s, m) => s + (Number(m.stock_eur) || 0), 0)} />
      )}

      {loading ? (
        <div className="min-h-[30vh] grid place-items-center" data-testid="stalled-loading">
          <div className="flex items-center gap-3 text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
            <span className="text-sm">Cruzando tu stock con lo dispensado…</span>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stalled-kpis">
            {[
              ["Moléculas paradas", data?.total_molecules ?? 0, "text-slate-900"],
              ["Referencias", data?.total_refs ?? 0, "text-slate-900"],
              ["Unidades en balda", data?.total_units ?? 0, "text-amber-700"],
              ["Valor a PVL", fmtEur(data?.total_eur), "text-rose-700"],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-lg p-5">
                <div className={`text-2xl font-bold font-mono-num ${color}`}>{value}</div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mt-1">{label}</div>
              </div>
            ))}
          </div>

          {!mols.length ? (
            <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-sm text-slate-500" data-testid="stalled-empty">
              {isMarca
                ? `No tienes marcas paradas: de todo lo que hay en stock ha salido algo en los últimos ${months} ${months === 1 ? "mes" : "meses"}.`
                : "No tienes genéricos parados en moléculas SOLO MARCA."}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm" data-testid="stalled-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <SortableTh k="gh" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Molécula (G.H.)</SortableTh>
                    <SortableTh k="lab_text" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Laboratorio</SortableTh>
                    <SortableTh k="stock_units" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4" align="right">Uds. en stock</SortableTh>
                    <SortableTh k="stock_eur" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4" align="right">Valor a PVL</SortableTh>
                    <SortableTh k="last_sale_date" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Última venta</SortableTh>
                    {!isMarca && (
                      <SortableTh k="marca_euros" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4" align="right">Vendes en marca</SortableTh>
                    )}
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visible.map((m) => [
                    <tr key={m.gh} className="hover:bg-slate-50" data-testid={`stalled-row-${encodeURIComponent(m.gh)}`}>
                      <td className="p-4 font-medium text-slate-900">{m.gh}</td>
                      <td className="p-4 text-xs text-slate-500">{m.laboratorios.join(", ")}</td>
                      <td className="p-4 text-right font-mono-num font-semibold text-amber-700">{m.stock_units}</td>
                      <td className="p-4 text-right font-mono-num font-semibold text-rose-700">{fmtEur(m.stock_eur)}</td>
                      <td className="p-4 text-xs" data-testid={`stalled-last-${encodeURIComponent(m.gh)}`}>
                        <LastSale item={m} />
                      </td>
                      {!isMarca && (
                        <td className="p-4 text-right font-mono-num text-slate-600">
                          {m.marca_units} uds · {fmtEur(m.marca_euros)}
                        </td>
                      )}
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setOpen((o) => ({ ...o, [m.gh]: !o[m.gh] }))}
                          className="text-[11px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 whitespace-nowrap"
                          data-testid={`stalled-toggle-${encodeURIComponent(m.gh)}`}
                        >
                          {open[m.gh] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {open[m.gh] ? "Ocultar" : `Ver ${m.refs} ${m.refs === 1 ? "referencia" : "referencias"}`}
                        </button>
                      </td>
                    </tr>,
                    open[m.gh] && (
                      <tr key={`${m.gh}-detail`} className="bg-slate-50/70">
                        <td colSpan={isMarca ? 6 : 7} className="px-4 pb-4">
                          <table className="w-full text-xs" data-testid={`stalled-detail-${encodeURIComponent(m.gh)}`}>
                            <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                              <tr>
                                <th className="py-2 text-left">CN</th>
                                <th className="py-2 text-left">Nombre comercial</th>
                                <th className="py-2 text-left">Laboratorio</th>
                                <th className="py-2 text-right">Stock</th>
                                <th className="py-2 text-right">PVL</th>
                                <th className="py-2 text-right">Valor a PVL</th>
                                <th className="py-2 text-left pl-4">Última venta</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {m.items.map((it) => (
                                <tr key={it.cn}>
                                  <td className="py-1.5 font-mono-num text-slate-500">{it.cn}</td>
                                  <td className="py-1.5 text-slate-700">{it.name}</td>
                                  <td className="py-1.5 text-slate-500">{it.laboratorio}</td>
                                  <td className="py-1.5 text-right font-mono-num">{it.stock_units}</td>
                                  <td className="py-1.5 text-right font-mono-num">{fmtEur(it.pvl)}</td>
                                  <td className="py-1.5 text-right font-mono-num font-semibold">{fmtEur(it.stock_eur)}</td>
                                  <td className="py-1.5 pl-4"><LastSale item={it} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ),
                  ])}
                </tbody>
              </table>
              {mols.length > visible.length && (
                <div className="p-4 border-t border-slate-200 text-center">
                  <button onClick={() => setShowAll(true)} className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900" data-testid="stalled-show-all">
                    Ver las {mols.length} moléculas
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
